import React, { useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Navbar from '../container/navbar';
import './style.css';

interface HeadLayoutProps {
  title: string;
  description: string;
  keywords: string;
  children?: React.ReactNode;
}

const HeadLayout: React.FC<HeadLayoutProps> = ({ title, description, keywords, children }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Function to check if the HTML element has the class "dark"
    const checkDarkClass = () => {
      const htmlElement = document.documentElement;
      const hasDarkClass = htmlElement.classList.contains('dark');
      setIsDark(hasDarkClass);
    };
// Run the initial check
checkDarkClass();

// Create a MutationObserver to watch for changes to the class attribute of the HTML element
const observer = new MutationObserver(checkDarkClass);
observer.observe(document.documentElement, { attributes: true });

// Cleanup function to disconnect the observer when the component unmounts
return () => {
  observer.disconnect();
};
}, []);

  // Blue color scheme for all pages
  const lightBgColor = '#e6f2ff'; // Light blue background
  const darkBgColor = '#0c2d48';  // Deep blue background for dark mode
  const lightAccentColor = '#0071c2'; // Primary blue - same as previously used
  const darkAccentColor = '#2196f3'; // Brighter blue for dark mode accent

  return (
    <HelmetProvider>
      <Helmet>
        <script src="https://unpkg.com/@themesberg/flowbite@1.1.1/dist/flowbite.bundle.js"></script>
        <meta charSet="utf-8" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="google-adsense-account" content="ca-pub-2623922203603929"></meta>
			  <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2623922203603929"
        crossOrigin="anonymous"></script>

		    <script async src="https://www.googletagmanager.com/gtag/js?id=G-MK1EHN59WY"></script>
		    <script src="/js/gtag.js"></script>

		    <meta name='impact-site-verification' defaultValue='d8d3b68a-6498-4c97-a5fe-03f5d34303e4'></meta>
		    <meta name='impact-site-verification' defaultValue='2236fe41-bc79-4f97-bdda-e15cd2abfc5b'></meta>
      </Helmet>
      <Navbar />

      <div
        className="main-content flex items-start content-wrapper min-h-full pt-16 relative overflow-x-hidden"
        style={{
          backgroundColor: isDark ? darkBgColor : lightBgColor,
          backgroundImage: isDark 
            ? `linear-gradient(to bottom, ${darkBgColor}, #051c30)` 
            : `linear-gradient(to bottom, ${lightBgColor}, #ffffff)`,
          backgroundAttachment: 'fixed',
        } as React.CSSProperties}
      >
        {/* Decorative background elements */}
        <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
          {/* Subtle blue gradient overlay */}
          <div 
            className="absolute inset-0 opacity-70"
            style={{
              backgroundImage: isDark
                ? `radial-gradient(circle at 20% 30%, rgba(41, 128, 185, 0.15) 0%, transparent 60%), 
                   radial-gradient(circle at 80% 70%, rgba(52, 152, 219, 0.1) 0%, transparent 50%)`
                : `radial-gradient(circle at 20% 30%, rgba(0, 113, 194, 0.07) 0%, transparent 60%),
                   radial-gradient(circle at 80% 70%, rgba(3, 169, 244, 0.05) 0%, transparent 50%)`,
            }}
          ></div>
          
          {/* Subtle grid pattern */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(${isDark ? 'rgba(33, 150, 243, 0.1)' : 'rgba(0, 113, 194, 0.1)'} 1px, transparent 1px), 
                               linear-gradient(to right, ${isDark ? 'rgba(33, 150, 243, 0.1)' : 'rgba(0, 113, 194, 0.1)'} 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              backgroundAttachment: 'fixed',
            }}
          ></div>
          
          {/* Light decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 dark:bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 dark:bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
       
        <div className='w-full flex justify-center min-h-screen relative z-10'>
          {children}
          </div>
      </div>
    </HelmetProvider>
  );
};

export default HeadLayout;
